<template>
  <div>
    <!-- Options -->
    <div v-if="renderOptions" class="wishlist__product-options">
      <ProductOptions
        :key="productToWishlist.product"
        :product="productToWishlist.product"
        :selectedOptions="productForm"
        :hideColorOption="true"
        @on-option-change="(key, value) => onOptionChange(key, value)"
        :toolTipPosition="isDesktopScreen ? `bottom` : `top`"
      />
    </div>

    <!-- Create New List screem -->
    <div v-if="isCreatingNewList" class="wishlist__new-list">
      <input
        type="text"
        name="listname"
        v-model="newListName"
        @input="clearError"
        placeholder="Enter name for list"
        class="wishlist__new-list-input-box"
        required
      />
      <div class="wishlist__new-list__error" v-if="error">{{ error }}</div>
      <div class="wishlist__new-list-cta">
        <button
          class="btn btn--secondary btn--medium wishlist__new-list-cta-save"
          @click="handleCreateNewList"
          :disabled="isCreateNewListCTADisabled()"
        >
          <span :class="{ 'wishlist--processing': isProcessing }" class="wishlist__cta-loader">Save</span>
        </button>
        <button
          v-if="wishlistContentDetailsList.length"
          class="btn wishlist__new-list-cta-new-list"
          @click="handleCreateNewListScreen(false)"
        >
          Add to Existing list
        </button>
      </div>
    </div>

    <!-- Add to Existing List screen -->
    <div v-else class="wishlist__select-list">
      <MultiSelect
        :items="listItems"
        placeholder="Select a list"
        :isExpanded="true"
        :selectedValues="selectedWishlistIds"
        @update-selected-value="handleSelectedList"
      />
      <div class="wishlist__select-list-cta">
        <button
          class="btn btn--secondary btn--medium wishlist__select-list-cta-save"
          @click="handleAddToExistingWishlist"
          :disabled="isAddToExistingCTADisabled()"
        >
          <span :class="{ 'wishlist--processing': isProcessing }" class="wishlist__cta-loader">Save</span>
        </button>
        <button
          v-if="wishlistContentDetailsList.length"
          class="btn wishlist__select-list-cta-new-list"
          @click="handleCreateNewListScreen(true)"
        >
          Create New List
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onBeforeMount } from 'vue';
  import { useStore } from 'vuex';
  import MultiSelect from '../../../global/multi-select.vue';
  import { addProductToMultipleWishlist, addToWishlist, createNewWishlist, markListAsPublic } from '../../api.js';
  import ProductOptions from '../../../pdp-first-fold/components/product-options/index.vue';

  const props = defineProps({
    handleModalClose: {
      type: Function,
      default: () => {},
    },

    showToast: {
      type: Function,
      default: () => {},
    },

    isDesktopScreen: {
      type: Boolean,
      default: false
    }
  });

  const store = useStore();

  const newListName = ref('');
  const selectedWishlistIds = ref([]);
  const error = ref('');
  const isProcessing = ref(false);
  const productForm = ref({
    Color: null,
    Size: null,
    Inseam: null,
    Outseam: null,
    selectedVariant: null,
  });

  const isCreatingNewList = computed(() => store.state.showCreateNewListScreen);
  const renderOptions = computed(() => store.state.renderOptionsInWishlistModal);
  const wishlistContentDetailsList = computed(() => store.state.wishlistContentDetailsList);
  const wishlistedProducts = computed(() => store.state.wishlistProducts);
  const productToWishlist = computed(() => store.state.productToWishlist);
  const listItems = computed(() =>
    wishlistContentDetailsList.value?.map((item) => ({ id: item.lid, name: item.lname }))
  );

  const emit = defineEmits(['wishlist-name-updated']); 

  onBeforeMount(() => {
    initialOptionsSetupForProductForm();
  });

  onMounted(() => {
    selectedWishlistIds.value = wishlistContentDetailsList.value.flatMap((item) =>
      item.listcontents
        .filter((prod) => prod.epi == productToWishlist.value?.variant?.selectedVariant?.id)
        .map(() => item.lid)
    );
  });

  const initialOptionsSetupForProductForm = () => {
    const { product, variant } = productToWishlist.value;

    if (variant?.Color) {
      productForm.value.Color = variant.Color;
    }

    // For Inseam and Outseam
    if (additionalPropertyOptions()?.length) {
      const inseamOutseamData = product.options.find((ele) => ele.name === 'Inseam' || ele.name === 'Outseam');
      inseamOutseamData && (productForm.value[inseamOutseamData['name']] = inseamOutseamData['values'][0]);
    }
  };

  const additionalPropertyOptions = () => {
    const { product } = productToWishlist.value;
    return product.additional_property_options != '' ? product.additional_property_options.split(',') : [];
  };

  const onOptionChange = (key, value) => {
    productForm.value[key] = value;
    const { product } = productToWishlist.value || {};

    // setting variant
    const variantValue = product?.options.map((option) => productForm.value[option.name]).join(' / ');
    const variant = product?.variants.find((item) => item.value === variantValue);

    productForm.value.selectedVariant = variant || null;
  };

  const isCreateNewListCTADisabled = () => {
    if (renderOptions.value) {
      return !productForm.value.selectedVariant || !newListName.value.trim() || isProcessing.value;
    }
    return !newListName.value.trim() || isProcessing.value;
  };

  const isAddToExistingCTADisabled = () => {
    if (renderOptions.value) {
      return !productForm.value.selectedVariant || selectedWishlistIds.value.length < 1 || isProcessing.value;
    }
    return selectedWishlistIds.value.length < 1 || isProcessing.value;
  };

  const handleCreateNewList = async () => {
    const newList = newListName.value.trim();

    const validationError = validateInput(newList);

    if (validationError) {
      error.value = validationError;
      return;
    }

    try {
      isProcessing.value = true;
      const listResponse = await createNewWishlist(newList);
      if (listResponse) {
        await addProductToWishlist(listResponse);
        markListAsPublic(listResponse.lid);
      }
    } catch (error) {
      console.error('Error creating list', error);
      store.dispatch('showWishlistModal', false);
      props.showToast({
        status: 'error',
        message: `Error! ${error?.msg || error?.[0]?.msg}`,
        customMessageClass: 'wishlist-error-select-option',
      });
    } finally {
      isProcessing.value = false;
    }
  };

  const validateInput = (listName) => {
    let error = '';

    if (isWishlistAlreadyExist(listName)) {
      error = 'Wishlist already exists';
    } else if (listName.length < 3) {
      error = 'Please enter at least 3 characters';
    } else if (listName.length > 50) {
      error = 'Please enter no more than 50 characters';
    } else if (!/^[A-Za-z0-9_!&$#@ ,’.'-]*$/.test(listName)) {
      error = "Only these special characters allowed: _!&$#@ ,’.'-";
    }

    return error;
  };

  const handleAddToExistingWishlist = async () => {
    try {
      isProcessing.value = true;

      const response = await addProductToMultipleWishlist(
        selectedWishlistIds.value,
        productToWishlist.value.product,
        renderOptions.value ? productForm.value : productToWishlist.value.variant
      );

      if (response) {
        const listObj = wishlistContentDetailsList.value.find((item) => item.lid === selectedWishlistIds.value[0]);
        store.dispatch('fetchWishlistItems');
        props.handleModalClose();
        props.showToast({
          status: 'success',
          title: selectedWishlistIds.value.length > 1 ? 'Item added to wishlists' : `Item added to ${listObj?.lname}`,
          enableElipsis: true,
        });
        handleToastForGuestUserAfterDelay();
      }
    } catch (error) {
      console.error('Error adding product to list', error);
      store.dispatch('showWishlistModal', false);
      props.showToast({
        status: 'error',
        message: `Error! ${error?.msg || error?.[0]?.msg}`,
        customMessageClass: 'wishlist-error-select-option',
      });
    } finally {
      isProcessing.value = false;
    }
  };

  const addProductToWishlist = async (newlyCreatedListResponse) => {
    let { product, variant } = productToWishlist.value;
    if (renderOptions.value) {
      variant = productForm.value;
    }
    const response = await addToWishlist(newlyCreatedListResponse.lid, product, variant);
    if (response) {
      store.dispatch('fetchWishlistItems');
      props.handleModalClose();
      props.showToast({
        status: 'success',
        title: `Item added to ${newlyCreatedListResponse?.lname}`,
        enableElipsis: true,
      });
      handleToastForGuestUserAfterDelay();
    }
  };

  const handleCreateNewListScreen = (value) => {
    store.dispatch('toggleCreateNewListScreen', value);
  };

  const handleSelectedList = (listIds) => {
    selectedWishlistIds.value = listIds;
  };

  // This will trigger a toast after 5 seconds for Sign in reminder when user is guest and has already added 5 items
  const handleToastForGuestUserAfterDelay = () => {
    const customerDetails = window?.getCustomerDetails?.();
    if (!customerDetails && wishlistedProducts.value.length == 4) {
      setTimeout(() => {
        props.showToast({
          status: 'error',
          templateType: 'wishlist:sign-in',
          enableElipsis: false,
          duration: 5000,
        });
      }, 3000);
    }
  };

  const isWishlistAlreadyExist = (newList) => {
    return wishlistContentDetailsList.value.some((item) => item.lname.toLowerCase() === newList.toLowerCase());
  };

  const clearError = () => {
    error.value = '';

    if (newListName.value.trim()) {
      emit('wishlist-name-updated', newListName.value.trim());
    } else {
      emit('wishlist-name-updated', '');
    }
  };
</script>
